import React, { FC, useEffect } from 'react';
import defaults from 'lodash/defaults';

import { config, yup } from 'data';
import { profileService } from 'services';
import { useForm, useLang, useMutation, useQueryInvalidate } from 'hooks';
import { CardView } from 'components/layout';
import { CountrySelect } from 'components/selects';
import { Button, Flex, Form, Input } from 'components/ui';
import { Profile } from 'types/models';
import { ProfileUpdateParams } from 'types/services';

type FormValues = ProfileUpdateParams;

const initialValues: FormValues = {
  name: '',
  companyName: '',
  countryCode: config.DEFAULT_COUNTRY,
  phone: '',
};

const validationSchema = yup.object({
  name: yup.string().required().min(config.NAME_MIN_LENGTH).max(config.NAME_MAX_LENGTH),
  companyName: yup.string().required().min(config.COMPANY_NAME_MIN_LENGTH).max(config.COMPANY_NAME_MAX_LENGTH),
  countryCode: yup.string().required().country(),
  phone: yup.string().required().phone(),
});

type ProfileDetailsProps = {
  profile: Profile;
};

const ProfileDetails: FC<ProfileDetailsProps> = ({ profile }) => {
  const form = useForm<FormValues>();
  const lang = useLang();
  const queryInvalidate = useQueryInvalidate();

  const invalidateClientQueries = async () => {
    await queryInvalidate([config.PROFILE_QUERY_KEY]);
  };

  const updateProfileMutation = useMutation({
    mutationFn: profileService.updateProfile,
    onSuccess: invalidateClientQueries,
    successNotification: lang.get('profile.details.success'),
  });

  const handleSubmit = async (values: FormValues) => {
    await updateProfileMutation.mutateAsync(values);

    return false;
  };

  useEffect(() => {
    form.setFieldsValue(defaults({}, profile, initialValues));
  }, [profile, form]);

  return (
    <CardView
      title={lang.get('profile.details.title')}
      caption={lang.get('profile.details.caption')}
    >
      <Form
        form={form}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onFinish={handleSubmit}
      >
        <Form.Item name="name" label={lang.get('common.form.fullName.label')}>
          <Input placeholder={lang.get('common.form.fullName.placeholder')} />
        </Form.Item>
        <Form.Item name="companyName" label={lang.get('common.form.company.label')}>
          <Input placeholder={lang.get('common.form.company.placeholder')} />
        </Form.Item>
        <Form.Item name="countryCode" label={lang.get('common.form.country.label')}>
          <CountrySelect />
        </Form.Item>
        <Form.Item name="phone" label={lang.get('common.form.phone.label')}>
          <Input.Phone />
        </Form.Item>
        <Form.Item name="email" label={lang.get('common.form.email.label')}>
          <Input.Email disabled />
        </Form.Item>
        <Flex justify="flex-end">
          <Button htmlType="submit" loading={updateProfileMutation.isPending}>
            {lang.get('common.actions.save')}
          </Button>
        </Flex>
      </Form>
    </CardView>
  );
};

export default ProfileDetails;
